'use client'

import { reportHandledException } from '@/lib/error-reporting'
import { toastTitle } from '@/lib/toast-title'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { CREDIT_PACK_DEFAULTS, type SubscriptionCycle, type PersonalPaidPlanCode, type CreditPackCode } from '@kiao-voice/core'
import { useTranslation } from 'react-i18next'
import {
  Button,
  ButtonLink,
  NumberedSection,
  SettingRow,
  StatusPill,
  Tag,
  type KiaoConfirmOptions,
  useKiaoConfirm,
  toast,
} from '@kiao-voice/ui-kit'
import { billingStatClassName, billingUnitClassName } from '@/components/billing-stat'
import { WorkspacePageHeader } from '@/components/workspace-indicator'
import { formatTaipeiDate, formatTaipeiMonthDay } from '@/lib/taipei-date'
import {
  useVoiceQuota,
  useWorkspaceScopedLoader,
  type WorkspaceScopedLoadContext,
} from '@kiao-voice/hooks'
import {
  BillingApiError,
  bindCard as bindCardApi,
  cancelSubscription,
  fetchBalance,
  fetchCards,
  fetchInvoiceSettings,
  fetchSubscription,
  fetchVoiceUsage,
  fetchWallet,
  purchaseCreditPackViaTappay,
  reactivateSubscription,
  removeCard,
  saveInvoiceSettings,
  setDefaultCard,
  subscribeViaTappay,
  topUpWalletViaTappay,
  type BalanceResponse,
  type CardSummary,
  type SubscriptionPayload,
} from '@/features/billing/api-client'
import { fetchMe, readStoredToken } from '@/features/auth/auth-storage'
import { listContexts } from '@/features/team/api-client'
import { useActiveContext } from '@/lib/use-active-context'
import { resolveActiveContext } from '@/lib/active-context-resolver'
import { CheckoutDialog } from '@/features/billing/checkout/checkout-dialog'
import type { CheckoutCouponContext } from '@/features/billing/checkout/checkout-panel'
import {
  createBindCardStrategy,
  createCreditPackStrategy,
  createSubscribeStrategy,
  createWalletTopUpStrategy,
} from '@/features/billing/checkout/checkout-strategies'
import { PLAN_CATALOG, subscribeAmountTwd } from '@/features/billing/plan-pricing'
import { trackBeginCheckout, type CommercePlan } from '@/lib/analytics'
import { AccountBanners } from '../account-banners'
import { DeleteAccountSection } from '../delete-account-section'
import { VoiceUsagePanel } from './voice-usage-panel'
import { WalletPanel } from '@/features/billing/wallet-panel'
import { InvoicePanel } from './invoice-panel'

/**
 * 帳號與用量 (/account) — design/app-account.html (Method B).
 *
 * Numbered sections (01–05): 用量 / 抵免額 / 目前訂閱 / 付款方式 / 發票資訊 —
 * all-white, no cards; each one is a shared <NumberedSection>（編號 + 標題 +
 * 髮絲線分層），節內的帳務數值列走 `.brow`。Plan
 * changes link to /pricing (查看方案 / 變更方案); the page also sells the
 * one-off credit pack + 抵免額 (account-credit) top-up. Payment flows open the
 * shared `<CheckoutDialog>` (issue #249) with the TapPay-hosted card fields.
 */
interface Props {
  /** Test seams. */
  getTokenFn?: () => string | null
  fetchSubscriptionFn?: typeof fetchSubscription
  fetchCardsFn?: typeof fetchCards
  fetchBalanceFn?: typeof fetchBalance
  fetchVoiceUsageFn?: typeof fetchVoiceUsage
  /** Test seam — defaults to `GET /organizations/contexts` (drives per-context view). */
  fetchContextsFn?: typeof listContexts
  /** Test seam — wallet balance for the checkout offset / card-free path. */
  fetchWalletFn?: typeof fetchWallet
  subscribeFn?: typeof subscribeViaTappay
  purchasePackFn?: typeof purchaseCreditPackViaTappay
  /** Test seam — wallet top-up via the unified checkout modal. */
  topUpFn?: typeof topUpWalletViaTappay
  cancelSubFn?: typeof cancelSubscription
  reactivateSubFn?: typeof reactivateSubscription
  bindCardFn?: typeof bindCardApi
  removeCardFn?: typeof removeCard
  setDefaultFn?: typeof setDefaultCard
  /** Test seams for the invoice panel — default to the real api-client calls. */
  invoiceLoadFn?: typeof fetchInvoiceSettings
  invoiceSaveFn?: typeof saveInvoiceSettings
  /** Pre-open the modal in tests / URL-intent flows. */
  initialAction?: PendingAction | null
  /** Skip the TapPay <Script> mount in tests. */
  skipScript?: boolean
  /** Redirect for 3DS — test seam. */
  redirect?: (url: string) => void
  /** Confirm seam — defaults to the shared kiaoConfirm dialog. */
  confirmFn?: (options: KiaoConfirmOptions) => Promise<boolean>
}

/**
 * The pack this page sells by default (加值 200 credits — NT$200). A UI
 * decision, so it stays here; the pack's price and credits come from the
 * shared seed defaults.
 *
 * Those defaults are what a fresh database starts with, NOT what the server
 * charges: `POST /billing/credit-packs` reads `app_settings` live, and an
 * operator can change it there. The amount shown here is a pre-flight estimate;
 * the charge is the server's number.
 */
const DEFAULT_PACK =
  CREDIT_PACK_DEFAULTS.find((pack) => pack.code === 'medium') ?? CREDIT_PACK_DEFAULTS[0]

type SubscribeAction = {
  type: 'subscribe'
  planCode: PersonalPaidPlanCode
  cycle: SubscriptionCycle
}
type PackAction = {
  type: 'pack'
  packCode: CreditPackCode
  credits: number
  price: number
}
type TopUpAction = {
  type: 'topup'
  amount: number
}
type BindAction = {
  type: 'bind'
}
type PendingAction = SubscribeAction | PackAction | TopUpAction | BindAction

/** Analytics ecommerce shape for a pending subscribe / pack action. */
function subscribePlan(action: SubscribeAction): CommercePlan {
  return {
    code: action.planCode,
    name: action.planCode,
    priceTwd: subscribeAmountTwd(action.planCode, action.cycle),
    cycle: action.cycle,
  }
}
function packPlan(action: PackAction): CommercePlan {
  return {
    code: `pack_${action.packCode}`,
    name: `Credit pack ${action.credits}`,
    priceTwd: action.price,
  }
}

/** Inline `.toast` payload — translated at render time. */
interface InlineToast {
  key: string
  params?: Record<string, unknown>
}

// The confirm-dialog provider (cancel sub / remove card) is now mounted once by
// the (app) route-group layout, so this page no longer wraps itself. Tests that
// render BillingPage in isolation supply their own KiaoConfirmProvider.
export function BillingPage({
  getTokenFn = readStoredToken,
  fetchSubscriptionFn = fetchSubscription,
  fetchCardsFn = fetchCards,
  fetchBalanceFn = fetchBalance,
  fetchVoiceUsageFn = fetchVoiceUsage,
  fetchContextsFn = listContexts,
  fetchWalletFn = fetchWallet,
  subscribeFn = subscribeViaTappay,
  purchasePackFn = purchaseCreditPackViaTappay,
  topUpFn = topUpWalletViaTappay,
  cancelSubFn = cancelSubscription,
  reactivateSubFn = reactivateSubscription,
  bindCardFn = bindCardApi,
  removeCardFn = removeCard,
  setDefaultFn = setDefaultCard,
  invoiceLoadFn = fetchInvoiceSettings,
  invoiceSaveFn = saveInvoiceSettings,
  initialAction,
  skipScript,
  redirect,
  confirmFn,
}: Props) {
  const { t, i18n } = useTranslation('web')
  const searchParams = useSearchParams()
  const kiaoConfirm = useKiaoConfirm()
  const confirm = confirmFn ?? kiaoConfirm
  // The selected workspace drives the per-context view: a team-MEMBER context
  // renders read-only billing on the team's SHARED pool; personal + team-owner
  // render the full personal billing. Switching workspaces re-runs `refresh`.
  const activeContext = useActiveContext()

  // Voice usage comes from the shared event-driven probe (mount + window
  // focus/visibility once the cached value is stale), replacing the inline
  // fetch that only refreshed on a full page `refresh()`. The fetcher is a
  // stable useCallback so the hook's focus/visibility listeners don't
  // re-subscribe each render.
  //
  // `voiceProbeSettled` is true once the first probe settles (resolved or
  // failed) — the panel keys off it to show a neutral loading line instead of
  // flashing its error line while the initial probe is in flight on a slow
  // `/voice-usage/me`.
  const [voiceProbeSettled, setVoiceProbeSettled] = useState(false)
  const voiceToken = getTokenFn()
  // No `activeContext` dep here: `useVoiceQuota` now subscribes to the
  // active-context store itself, so the re-probe on a workspace switch is the
  // hook's job for every consumer (web AND desktop, which had no equivalent
  // workaround and so kept a stale `exhausted` after switching). Re-adding the
  // dep would make the identity change fire a SECOND, redundant probe alongside
  // the subscription's — two `/voice-usage/me` calls per switch.
  const fetchUsage = useCallback(async () => {
    try {
      return await fetchVoiceUsageFn()
    } finally {
      setVoiceProbeSettled(true)
    }
  }, [fetchVoiceUsageFn])
  const voiceQuota = useVoiceQuota({ enabled: Boolean(voiceToken), fetchUsage })

  // URL intent → which action to auto-open (validated against allow-list,
  // never used as the raw POST body).
  const intentAction = useMemo<PendingAction | null>(() => {
    if (initialAction) return initialAction
    const rawSubscribe = searchParams?.get('subscribe') ?? null
    const rawCycle = searchParams?.get('cycle') ?? null
    const intent = searchParams?.get('intent') ?? null
    if (rawSubscribe === 'creator' || rawSubscribe === 'pro') {
      const cycle: SubscriptionCycle = rawCycle === 'yearly' ? 'yearly' : 'monthly'
      return { type: 'subscribe', planCode: rawSubscribe, cycle }
    }
    if (intent === 'pack') {
      // Honor explicit pack links and default to medium for legacy callers.
      const rawPack = searchParams?.get('pack') ?? null
      const packTile = CREDIT_PACK_DEFAULTS.find((p) => p.code === rawPack) ?? DEFAULT_PACK
      return {
        type: 'pack',
        packCode: packTile.code,
        credits: packTile.credits,
        price: packTile.priceTwd,
      }
    }
    return null
  }, [searchParams, initialAction])

  const [subscription, setSubscription] = useState<SubscriptionPayload | null>(null)
  const [allowanceSchedule, setAllowanceSchedule] = useState<NonNullable<
    Awaited<ReturnType<typeof fetchSubscription>>['allowanceSchedule']
  > | null>(null)
  const [cards, setCards] = useState<CardSummary[]>([])
  const [balance, setBalance] = useState<BalanceResponse | null>(null)
  // Org wallet (儲值金) balance for the checkout offset + card-free path; null when
  // the flag is off or the fetch failed (→ full cash price, card required).
  const [wallet, setWallet] = useState<{ enabled: boolean; total: number } | null>(null)
  // Set when the signed-in user occupies a seat in someone ELSE's team:
  // their voice + credits come from the team, so the page must not offer
  // personal packs (the API 422s them anyway) and the balance is the
  // SHARED pool, not a personal one.
  const [seatedOrgName, setSeatedOrgName] = useState<string | null>(null)
  // True when the user OWNS a live team subscription (a team-owner context is
  // only returned when the owned org has an active team sub). Used to warn,
  // before reactivating a pending-cancel personal sub, that resuming it means
  // paying for BOTH the personal and the team plan (#148).
  const [ownsLiveTeamSub, setOwnsLiveTeamSub] = useState(false)
  // Sourced from /auth/me — pre-fills the cardholder email and the
  // personal-invoice note.
  const [userEmail, setUserEmail] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  // `pending` is what the modal is currently configured for. Once non-null
  // it stays non-null until the next action click — we never reset it on
  // close, because that would unmount the TapPay card form and TPDirect
  // can't re-initialise its iframes after the divs leave the DOM.
  // Instead we toggle `modalOpen` for visibility only.
  const [pending, setPending] = useState<PendingAction | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  // Bumped on every modal OPEN. Folded into the panel's `resetKey` so reopening
  // the SAME target (e.g. buy a card-free pack twice in one session) re-rolls the
  // idempotency nonce + clears any prior coupon/forceCard — the original keyed
  // this off a fresh `pending` object per open; the derived resetKey string would
  // otherwise be identical across reopens and dedup the second purchase.
  const [openSeq, setOpenSeq] = useState(0)
  // Inline `.toast` confirmations per panel (prototype behavior); API
  // errors stay on the shared toast. Stored as i18n key + params (not pre-translated
  // strings) so they re-localize on a language switch.
  const [subToast, setSubToast] = useState<InlineToast | null>(null)
  const [cardToast, setCardToast] = useState<InlineToast | null>(null)
  const [packToast, setPackToast] = useState(false)
  // Bumped after any checkout success so the self-fetching panels (wallet balance,
  // invoice settings) — which this page's `refresh()` doesn't own — re-load. A
  // wallet top-up / in-checkout invoice edit happens inside the shared modal.
  const [reloadSignal, setReloadSignal] = useState(0)
  const suppressIntentAfterWorkspaceSwitchRef = useRef(false)

  // Open the modal if the URL signaled intent — but only after the initial
  // load resolves, so we know whether the user already has an active sub.
  useEffect(() => {
    if (loading) return
    if (suppressIntentAfterWorkspaceSwitchRef.current) return
    if (pending) return
    if (!intentAction) return
    // Don't auto-open subscribe if they already have an active sub.
    if (
      intentAction.type === 'subscribe' &&
      subscription &&
      subscription.status !== 'canceled' &&
      !subscription.cancelAtPeriodEnd
    )
      return
    setPending(intentAction)
    setModalOpen(true)
    setOpenSeq((s) => s + 1)
  }, [loading, intentAction, pending, subscription])

  // begin_checkout when a subscribe/pack payment modal opens (not bind-card,
  // which isn't a purchase). Keyed on the pending action's identity so it fires
  // once per distinct action — even if the action changes while the modal stays
  // open — and never twice for the same one.
  const checkoutTrackedRef = useRef<PendingAction | null>(null)
  useEffect(() => {
    if (!modalOpen || !pending) {
      checkoutTrackedRef.current = null
      return
    }
    if (checkoutTrackedRef.current === pending) return
    if (pending.type === 'subscribe') {
      checkoutTrackedRef.current = pending
      trackBeginCheckout(subscribePlan(pending))
    } else if (pending.type === 'pack') {
      checkoutTrackedRef.current = pending
      trackBeginCheckout(packPlan(pending))
    }
  }, [modalOpen, pending])

  const refreshWorkspace = useCallback(
    async ({ workspaceKey, isCurrent }: WorkspaceScopedLoadContext) => {
      const token = getTokenFn()
      if (!token) {
        if (isCurrent()) setLoading(false)
        return
      }
      if (isCurrent()) setLoadFailed(false)
      try {
        const [subResp, cardsResp, balanceResp, meResp, contextsResp, walletResp] =
          await Promise.all([
            fetchSubscriptionFn(),
            fetchCardsFn(),
            fetchBalanceFn(),
            // /auth/me is the source of truth for the user's email — used by
            // the cardholder prefill + invoice note.
            fetchMe(token).catch((caughtError) => {
              reportHandledException(caughtError, {
                source: 'app/app/account/billing/billing-page.tsx',
              })
              return null
            }),
            // Switchable contexts drive the seated-member read-only view. A
            // Team workspace must fail closed when its role cannot be resolved.
            fetchContextsFn().catch((caughtError) => {
              reportHandledException(caughtError, {
                source: 'app/app/account/billing/billing-page.tsx',
              })
              return null
            }),
            // Wallet balance powers the checkout offset / card-free path. A failure
            // must not break billing — fall back to "no wallet" (full cash price).
            fetchWalletFn().catch((caughtError) => {
              reportHandledException(caughtError, {
                source: 'app/app/account/billing/billing-page.tsx',
              })
              return null
            }),
          ])
        if (!isCurrent()) return
        const resolved = resolveActiveContext(contextsResp, workspaceKey)
        if (workspaceKey !== 'personal' && resolved.kind === 'unknown') {
          setLoadFailed(true)
          toast.error(toastTitle(t('billing.errorLoad')))
          return
        }
        setSubscription(subResp.subscription)
        setAllowanceSchedule(subResp.allowanceSchedule ?? null)
        setCards(cardsResp.cards)
        setBalance(balanceResp)
        setWallet(walletResp ? { enabled: walletResp.enabled, total: walletResp.total } : null)
        // Voice usage is owned by the shared useVoiceQuota probe above, not this
        // bundled refresh — it stays fresh via the hook's mount + focus listeners.
        if (meResp) setUserEmail(meResp.user.email)
        // Read-only seated view only when the SELECTED workspace is a team-MEMBER
        // context — keyed off the active context, not a static `/me` role.
        setSeatedOrgName(resolved.kind === 'team-member' ? resolved.orgName : null)
        // A team-owner context is present in the list only when the owned org has
        // a LIVE team subscription — that's exactly the double-billing precondition.
        setOwnsLiveTeamSub(
          (contextsResp ?? []).some((c) => c.mode === 'team' && c.role === 'owner')
        )
      } catch (cause) {
        reportHandledException(cause, { source: 'app/app/account/billing/billing-page.tsx' })
        if (isCurrent()) {
          setLoadFailed(true)
          toast.error(cause instanceof BillingApiError ? cause.message : toastTitle(t('billing.errorLoad')))
        }
      } finally {
        if (isCurrent()) setLoading(false)
      }
    },
    [
      getTokenFn,
      fetchSubscriptionFn,
      fetchCardsFn,
      fetchBalanceFn,
      fetchContextsFn,
      fetchWalletFn,
      t,
    ]
  )

  const clearWorkspace = useCallback(() => {
    suppressIntentAfterWorkspaceSwitchRef.current = true
    setLoading(true)
    setLoadFailed(false)
    setSubscription(null)
    setAllowanceSchedule(null)
    setCards([])
    setBalance(null)
    setWallet(null)
    setSeatedOrgName(null)
    setOwnsLiveTeamSub(false)
    setModalOpen(false)
    setPending(null)
    setSubToast(null)
    setCardToast(null)
    setPackToast(false)
    setVoiceProbeSettled(false)
    checkoutTrackedRef.current = null
  }, [])

  const { reload: reloadWorkspace } = useWorkspaceScopedLoader({
    clear: clearWorkspace,
    load: refreshWorkspace,
  })

  const refresh = useCallback(() => reloadWorkspace(), [reloadWorkspace])

  // The product-specific checkout configuration for the shared <CheckoutDialog>:
  // which strategy (API + analytics) to run, the order-summary inputs, and the
  // copy/test hooks. The shared panel owns the breakdown, card form, NT$0 path,
  // and every server-outcome mapping (issue #249).
  const checkout = useMemo(() => {
    if (!pending) return null
    if (pending.type === 'subscribe') {
      const amount = subscribeAmountTwd(pending.planCode, pending.cycle)
      const planName = t(
        `billing.planCard${pending.planCode.charAt(0).toUpperCase()}${pending.planCode.slice(1)}Name`
      )
      return {
        strategy: createSubscribeStrategy({
          planCode: pending.planCode,
          cycle: pending.cycle,
          amount,
          subscribeFn,
          getTokenFn,
          t,
        }),
        title: t('billing.modalSubscribeTitle'),
        subtitle: t('billing.modalSummarySubscribe', {
          plan: planName,
          cycle: pending.cycle === 'yearly' ? t('billing.cycleYearly') : t('billing.cycleMonthly'),
        }),
        amount,
        coupon: {
          product: 'subscription',
          planCode: pending.planCode,
          cycle: pending.cycle,
        } as CheckoutCouponContext,
        formTestId: 'billing-subscribe-form',
        submitLabelOverride: undefined as string | undefined,
        resetKey: `sub-${pending.planCode}-${pending.cycle}-${openSeq}`,
      }
    }
    if (pending.type === 'pack') {
      return {
        strategy: createCreditPackStrategy({
          packCode: pending.packCode,
          credits: pending.credits,
          price: pending.price,
          purchasePackFn,
          getTokenFn,
          t,
        }),
        title: t('billing.modalPackTitle'),
        subtitle: t('billing.modalSummaryPack', { credits: pending.credits }),
        amount: pending.price,
        coupon: { product: 'credit_pack', packCode: pending.packCode } as CheckoutCouponContext,
        formTestId: 'billing-pack-form',
        submitLabelOverride: undefined as string | undefined,
        resetKey: `pack-${pending.packCode}-${openSeq}`,
      }
    }
    if (pending.type === 'topup') {
      return {
        strategy: createWalletTopUpStrategy({ amount: pending.amount, topUpFn, getTokenFn, t }),
        title: t('billing.walletTopUpModalTitle'),
        subtitle: t('billing.walletTopUpModalSubtitle', { amount: pending.amount }),
        amount: pending.amount,
        // A top-up takes no coupon and no wallet offset (you can't fund a wallet
        // from itself) → coupon:null hides the coupon field + offset. The summary
        // column still renders (金額明細 + 發票; the invoice is issued at top-up).
        coupon: null,
        formTestId: 'billing-topup-form',
        submitLabelOverride: undefined as string | undefined,
        resetKey: `topup-${pending.amount}-${openSeq}`,
      }
    }
    return {
      strategy: createBindCardStrategy({ bindCardFn, getTokenFn, t }),
      title: t('billing.bindCardModalTitle'),
      subtitle: t('billing.bindCardModalSubtitle'),
      amount: 0,
      // Bind-card is not a purchase: no coupon, no breakdown, always a card.
      coupon: null,
      formTestId: 'billing-bind-form',
      submitLabelOverride: t('billing.bindCardSubmit'),
      resetKey: `bind-${openSeq}`,
    }
  }, [pending, openSeq, subscribeFn, purchasePackFn, topUpFn, bindCardFn, getTokenFn, t])

  // Per-product success feedback, run by the panel after it hides the dialog.
  const handleCheckoutSuccess = useCallback(async () => {
    if (!pending) return
    // Re-load the self-fetching panels (wallet balance + invoice settings) — a
    // top-up changed the balance, and a purchase may have saved invoice settings.
    setReloadSignal((s) => s + 1)
    if (pending.type === 'subscribe') {
      const tile = PLAN_CATALOG.find((p) => p.code === pending.planCode)
      // A plan change flips the voice quota (free monthly cap → unlimited, or a
      // new cap), so re-probe it alongside the rest — the usage panel reads from
      // the shared hook, which `refresh()` no longer touches.
      await Promise.all([refresh(), voiceQuota.refresh()])
      toast.success(t('billing.toastSubscribed', { credits: tile?.monthlyCredits ?? 0 }))
    } else if (pending.type === 'pack') {
      await refresh()
      setPackToast(true)
    } else if (pending.type === 'topup') {
      await refresh()
      toast.success(t('billing.walletTopUpSuccess', { amount: pending.amount }))
    } else {
      await refresh()
      setCardToast({ key: 'billing.toastCardBound' })
    }
  }, [pending, refresh, voiceQuota.refresh, t])

  // The wallet stopped covering the price server-side (card_required) → re-fetch
  // it so the rest of the page (WalletPanel, balance) reflects the new total. The
  // panel itself already dropped the offset + showed the card form.
  const handleCardRequired = useCallback(() => {
    void refresh()
  }, [refresh])

  const handleCancel = useCallback(async () => {
    const token = getTokenFn()
    if (!token) return
    const ok = await confirm({
      title: t('billing.cancelConfirmTitle'),
      message: t('billing.cancelConfirmMessage'),
      confirmText: t('billing.cancelButton'),
      cancelText: t('billing.cancelConfirmKeep'),
    })
    if (!ok) return
    try {
      const resp = await cancelSubFn()
      setSubscription(resp.subscription)
      setAllowanceSchedule(
        resp.subscription?.currentPeriodEnd
          ? { kind: 'access_ends', at: resp.subscription.currentPeriodEnd }
          : null
      )
      setSubToast({
        key: 'billing.cancelPending',
        params: { date: formatTaipeiDate(resp.subscription?.currentPeriodEnd ?? null) },
      })
    } catch (cause) {
      reportHandledException(cause, { source: 'app/app/account/billing/billing-page.tsx' })
      toast.error(cause instanceof BillingApiError ? cause.message : t('billing.errorCancel'))
    }
  }, [getTokenFn, confirm, cancelSubFn, t])

  const handleReactivate = useCallback(async () => {
    const token = getTokenFn()
    if (!token) return
    // Reactivating undoes the owner-conversion's period-end cancel. If the user
    // already owns a live team sub, resuming the personal sub means paying for
    // BOTH plans next cycle — warn explicitly instead of silently double-billing.
    const ok = await confirm({
      title: ownsLiveTeamSub
        ? t('billing.reactivateTeamConflictTitle')
        : t('billing.reactivateConfirmTitle'),
      message: ownsLiveTeamSub
        ? t('billing.reactivateTeamConflictMessage')
        : t('billing.reactivateConfirmMessage'),
      confirmText: t('billing.reactivateButton'),
      cancelText: t('billing.modalCancel'),
    })
    if (!ok) return
    try {
      const resp = await reactivateSubFn()
      setSubscription(resp.subscription)
      setAllowanceSchedule(
        resp.subscription?.nextAllowanceAt || resp.subscription?.currentPeriodEnd
          ? {
              kind: 'plan_refill',
              at: (resp.subscription?.nextAllowanceAt ?? resp.subscription?.currentPeriodEnd)!,
            }
          : null
      )
      setSubToast({ key: 'billing.toastReactivated' })
    } catch (cause) {
      reportHandledException(cause, { source: 'app/app/account/billing/billing-page.tsx' })
      toast.error(cause instanceof BillingApiError ? cause.message : t('billing.errorReactivate'))
    }
  }, [getTokenFn, confirm, reactivateSubFn, ownsLiveTeamSub, t])

  const handleRemoveCard = useCallback(
    async (cardId: number) => {
      const token = getTokenFn()
      if (!token) return
      const ok = await confirm({
        title: t('billing.removeCardConfirmTitle'),
        message: t('billing.removeCardConfirmMessage'),
        confirmText: t('billing.removeCardConfirmCta'),
        cancelText: t('billing.modalCancel'),
      })
      if (!ok) return
      try {
        await removeCardFn(cardId)
        await refresh()
        setCardToast({ key: 'billing.toastCardRemoved' })
      } catch (cause) {
        reportHandledException(cause, { source: 'app/app/account/billing/billing-page.tsx' })
        toast.error(cause instanceof BillingApiError ? cause.message : t('billing.errorRemoveCard'))
      }
    },
    [getTokenFn, confirm, removeCardFn, refresh, t]
  )

  const handleSetDefault = useCallback(
    async (cardId: number) => {
      const token = getTokenFn()
      if (!token) return
      try {
        await setDefaultFn(cardId)
        await refresh()
        setCardToast({ key: 'billing.toastDefaultUpdated' })
      } catch (cause) {
        reportHandledException(cause, { source: 'app/app/account/billing/billing-page.tsx' })
        toast.error(cause instanceof BillingApiError ? cause.message : t('billing.errorSetDefault'))
      }
    },
    [getTokenFn, setDefaultFn, refresh, t]
  )

  const openPack = useCallback(() => {
    setPackToast(false)
    setPending({
      type: 'pack',
      packCode: DEFAULT_PACK.code,
      credits: DEFAULT_PACK.credits,
      price: DEFAULT_PACK.priceTwd,
    })
    setModalOpen(true)
    setOpenSeq((s) => s + 1)
  }, [])
  const openBindCard = useCallback(() => {
    setPending({ type: 'bind' })
    setModalOpen(true)
    setOpenSeq((s) => s + 1)
  }, [])
  const openWalletTopUp = useCallback((amount: number) => {
    setPending({ type: 'topup', amount })
    setModalOpen(true)
    setOpenSeq((s) => s + 1)
  }, [])

  // A team subscription is surfaced READ-ONLY on /account in a team context (E):
  // it is not a personal sub, so the personal plan-change / cancel controls stay
  // hidden for it — team seats are managed on the Team page.
  const isTeamSub = subscription?.planCode === 'team'
  const hasPersonalSub = Boolean(subscription && subscription.status !== 'canceled' && !isTeamSub)
  const isComplimentary = Boolean(hasPersonalSub) && subscription?.fundingSource === 'complimentary'
  const isAppleManaged = Boolean(hasPersonalSub) && subscription?.provider === 'apple'
  const isPastDue = subscription?.status === 'past_due'
  // Credits can be fractional internally, but the balance reads cleaner as a
  // whole number — floor so we never overstate what's spendable.
  const balanceTotal = Math.floor(balance?.total ?? 0)
  const willCancel = subscription?.cancelAtPeriodEnd === true
  const allowanceScheduleText = seatedOrgName
    ? t('billing.usageScheduleBySource')
    : allowanceSchedule
      ? t(
          allowanceSchedule.kind === 'plan_refill'
            ? subscription?.cycle === 'yearly'
              ? 'billing.usageSchedulePlanYearly'
              : 'billing.usageSchedulePlan'
            : allowanceSchedule.kind === 'free_refill'
              ? 'billing.usageScheduleFree'
              : isComplimentary
                ? 'billing.usageScheduleComplimentary'
                : 'billing.usageScheduleEnding',
          {
            date: formatTaipeiMonthDay(
              allowanceSchedule.at,
              i18n.resolvedLanguage ?? i18n.language
            ),
          }
        )
      : t('billing.usageScheduleBySource')
  const statusKey = (status: SubscriptionPayload['status']): string =>
    `billing.status${status.charAt(0).toUpperCase()}${status
      .slice(1)
      .replace(/_./g, (m) => m.charAt(1).toUpperCase())}`
  // Cards/invoice are hidden for seated members WITHOUT a personal sub:
  // cards only ever pay personal subs/packs, both blocked while seated. A
  // member with a PAUSED personal sub keeps the section — the resume-time
  // renewal charges their default card, so they must be able to update it.
  const showCardPanel = !isAppleManaged && !(seatedOrgName && !hasPersonalSub)
  // Apple owns recurring subscription billing, but invoice details are still
  // used by web-only credit-pack purchases.
  const showInvoicePanel = !(seatedOrgName && !hasPersonalSub)

  if (loading) {
    return (
      <p className="note" data-testid="billing-loading">
        {t('billing.loading')}
      </p>
    )
  }

  if (loadFailed) {
    return (
      <div data-testid="billing-page" style={{ maxWidth: 880 }}>
        <WorkspacePageHeader title={t('billing.heading')} subtitle={t('billing.subhead')} />
        <p className="alert show" role="alert" data-testid="billing-load-error">
          {t('billing.errorLoad')}
        </p>
      </div>
    )
  }

  const planLine =
    subscription && subscription.planCode !== 'team'
      ? isAppleManaged
        ? t(
            subscription.planCode === 'pro'
              ? 'billing.planCardProName'
              : 'billing.planCardCreatorName'
          )
        : isComplimentary
          ? t('billing.complimentaryPlanLine', {
              plan: t(
                subscription.planCode === 'pro'
                  ? 'billing.planCardProName'
                  : 'billing.planCardCreatorName'
              ),
            })
          : subscription.cycle === 'yearly'
            ? t(
                subscription.planCode === 'pro'
                  ? 'billing.planChoiceProYearly'
                  : 'billing.planChoiceCreatorYearly',
                { price: subscribeAmountTwd(subscription.planCode, 'yearly') }
              )
            : t(
                subscription.planCode === 'pro'
                  ? 'billing.planChoicePro'
                  : 'billing.planChoiceCreator',
                { price: subscribeAmountTwd(subscription.planCode, 'monthly') }
              )
      : null

  // 純團隊席次（沒有個人訂閱）→ 03 節顯示「團隊席次」。同時持有個人訂閱的席次成員走
  // 目前訂閱那一支，他自己的方案/取消控制項才會照常渲染。
  const isPureTeamSeat = !isTeamSub && !hasPersonalSub && Boolean(seatedOrgName)

  return (
    <div data-testid="billing-page" style={{ maxWidth: 880 }}>
      <WorkspacePageHeader title={t('billing.heading')} subtitle={t('billing.subhead')} />

      {getTokenFn() && <AccountBanners onChanged={refresh} />}

      {isPastDue && (
        <p className="alert show" role="alert" data-testid="billing-past-due-banner">
          {t('billing.pastDueBannerHeading')} — {t('billing.pastDueBannerBody')}
        </p>
      )}

      {/* 01 用量 — 轉錄額度 + 語音輸入（兩種配額，同一視覺語法） */}
      <NumberedSection
        number="01"
        data-testid="billing-usage"
        title={t('billing.secUsageTitle')}
        description={<span data-testid="billing-allowance-schedule">{allowanceScheduleText}</span>}
        className={isPastDue ? 'border-t-0 mt-[var(--gap-md)]' : 'border-t-0'}
      >
        <SettingRow
          label={
            seatedOrgName || isTeamSub ? t('billing.balanceHeadingTeam') : t('billing.creditsTitle')
          }
          description={t('billing.creditsDesc')}
          control={
            <span className="flex flex-wrap items-center justify-end gap-3.5 text-right">
              <span className={billingStatClassName} data-testid="billing-balance-total">
                {balanceTotal}
              </span>
              <span className={billingUnitClassName}>
                {t('billing.balanceUnit', { minutes: balanceTotal })}
              </span>
              {!seatedOrgName && (
                <Button
                  variant="secondary"
                  size="sm"
                  data-testid="billing-pack-buy"
                  onClick={openPack}
                >
                  {t('billing.creditsAddCta')}
                </Button>
              )}
            </span>
          }
        />

        {/* `isProbing` matters beyond the FIRST probe: a workspace switch
            clears `usage` and immediately re-probes, and the panel renders a
            null usage as an ERROR once settled — without this the user would
            see the error line flash on every switch. */}
        <VoiceUsagePanel
          usage={voiceQuota.usage}
          loading={Boolean(voiceToken) && (!voiceProbeSettled || voiceQuota.isProbing)}
        />

        {!seatedOrgName && packToast && (
          <p className="toast show" data-testid="billing-pack-toast">
            {t('billing.creditPackSuccessHeading')}
          </p>
        )}
        {seatedOrgName && (
          <p className="note" data-testid="billing-team-managed-note">
            {t('billing.teamMemberTopupNote')}
          </p>
        )}
      </NumberedSection>

      {/* 02 抵免額（真實金額；面板於 flag off 時自動隱藏） */}
      {wallet?.enabled && (
        <NumberedSection
          number="02"
          data-testid="billing-wallet-section"
          title={t('billing.walletHeading')}
          description={t('billing.secWalletNote')}
        >
          <WalletPanel
            getTokenFn={getTokenFn}
            fetchWalletFn={fetchWalletFn}
            onTopUp={openWalletTopUp}
            reloadSignal={reloadSignal}
            seated={Boolean(seatedOrgName)}
          />
        </NumberedSection>
      )}

      {/* 03 目前訂閱 */}
      <NumberedSection
        number="03"
        data-testid="billing-subscription"
        title={
          isPureTeamSeat ? (
            <span data-testid="billing-team-seat">
              {t('billing.teamSeatActive', { org: seatedOrgName })}
            </span>
          ) : (
            t('billing.currentSubscription')
          )
        }
        description={
          isPureTeamSeat
            ? t('billing.teamSeatBody')
            : // The note describes the recurring charge — meaningless for a free
              // user with no subscription, so show it only when one exists.
              hasPersonalSub || isTeamSub
              ? t(
                  isComplimentary
                    ? 'billing.secComplimentaryNote'
                    : isAppleManaged
                      ? 'billing.secAppleManagedNote'
                      : 'billing.secSubscriptionNote'
                )
              : undefined
        }
      >
        {isPureTeamSeat ? null : (
          <>
            {isTeamSub ? (
              <div className="mt-1.5" data-testid="billing-team-subscription">
                <SettingRow
                  label={t('billing.fieldPlan')}
                  control={
                    <span className="flex flex-wrap items-center justify-end gap-3.5 text-right">
                      <span data-testid="billing-current-plan">
                        {t('billing.teamPlanLine', { seats: subscription!.seats ?? 0 })}
                      </span>
                    </span>
                  }
                />
                <SettingRow
                  label={willCancel ? t('billing.fieldSubEnd') : t('billing.fieldNextCharge')}
                  control={
                    <span className="flex flex-wrap items-center justify-end gap-3.5 text-right">
                      <span className="num">
                        {formatTaipeiDate(subscription!.currentPeriodEnd)}
                      </span>
                    </span>
                  }
                />
                <p className="note" style={{ marginTop: 'var(--gap-md)' }}>
                  {t('billing.teamManageNote')}{' '}
                  <a
                    className="link"
                    href="/app/account/team"
                    data-testid="billing-team-manage-link"
                  >
                    {t('billing.teamManageCta')}
                  </a>
                </p>
              </div>
            ) : hasPersonalSub ? (
              <div className="mt-1.5" data-testid="billing-current-subscription">
                <SettingRow
                  label={t('billing.fieldPlan')}
                  description={<span data-testid="billing-current-plan">{planLine}</span>}
                  control={
                    <span className="flex flex-wrap items-center justify-end gap-3.5 text-right">
                      <StatusPill
                        tone={willCancel ? 'muted' : 'default'}
                        data-testid="billing-sub-status"
                      >
                        <span className="meta">
                          {isComplimentary
                            ? t('billing.statusComplimentary')
                            : willCancel
                              ? t('billing.statusCancelAtPeriodEnd')
                              : t(statusKey(subscription!.status))}
                        </span>
                      </StatusPill>
                    </span>
                  }
                />
                {/* A period-end cancel will NOT be charged again → show the END
                    date, never a (misleading) next-charge date. */}
                {isComplimentary ? (
                  <SettingRow
                    data-testid="billing-complimentary-end-row"
                    label={t('billing.fieldComplimentaryEnd')}
                    control={
                      <span className="flex flex-wrap items-center justify-end gap-3.5 text-right">
                        <span className="num">
                          {formatTaipeiDate(
                            subscription!.complimentaryEndsAt ?? subscription!.currentPeriodEnd
                          )}
                        </span>
                      </span>
                    }
                  />
                ) : willCancel ? (
                  <SettingRow
                    data-testid="billing-sub-end-row"
                    label={t('billing.fieldSubEnd')}
                    control={
                      <span className="flex flex-wrap items-center justify-end gap-3.5 text-right">
                        <span className="num">
                          {formatTaipeiDate(subscription!.currentPeriodEnd)}
                        </span>
                      </span>
                    }
                  />
                ) : (
                  <SettingRow
                    label={t('billing.fieldNextCharge')}
                    control={
                      <span className="flex flex-wrap items-center justify-end gap-3.5 text-right">
                        <span className="num">
                          {formatTaipeiDate(subscription!.currentPeriodEnd)}
                        </span>
                      </span>
                    }
                  />
                )}
                {isAppleManaged ? (
                  <p className="note" data-testid="billing-apple-managed-note">
                    {t('billing.appleManagedGuidance')}
                  </p>
                ) : (
                  <div className="mt-[var(--gap-md)] flex flex-wrap gap-2">
                    <ButtonLink
                      variant="secondary"
                      size="sm"
                      href="/pricing"
                      data-testid="billing-change-plan"
                    >
                      {t(isComplimentary ? 'billing.startPaidPlanCta' : 'billing.changePlanCta')}
                    </ButtonLink>
                    {!isComplimentary && !willCancel && (
                      <Button
                        variant="secondary"
                        size="sm"
                        data-testid="billing-cancel-button"
                        onClick={() => void handleCancel()}
                      >
                        {t('billing.cancelButton')}
                      </Button>
                    )}
                    {willCancel && (
                      <Button
                        variant="primary"
                        size="sm"
                        data-testid="billing-reactivate-button"
                        onClick={() => void handleReactivate()}
                      >
                        {t('billing.reactivateButton')}
                      </Button>
                    )}
                  </div>
                )}
                {subToast && (
                  <p className="toast show" data-testid="billing-sub-toast">
                    {t(subToast.key, subToast.params)}
                  </p>
                )}
              </div>
            ) : (
              /* Free, no team seat — a single row: muted 未訂閱 pill + 查看方案 ↗ */
              <div className="mt-1.5">
                <SettingRow
                  data-testid="billing-no-subscription"
                  label={t('billing.fieldPlan')}
                  description={t('billing.freeStateDesc')}
                  control={
                    <span className="flex flex-wrap items-center justify-end gap-3.5 text-right">
                      <StatusPill tone="muted" data-testid="billing-sub-status-free">
                        <span className="meta">{t('billing.statusFree')}</span>
                      </StatusPill>
                      <ButtonLink
                        size="sm"
                        href="/pricing"
                        target="_blank"
                        rel="noopener"
                        data-testid="billing-view-plans"
                      >
                        {t('billing.viewPlansCta')}{' '}
                        <span className="meta" aria-hidden="true">
                          ↗
                        </span>
                      </ButtonLink>
                    </span>
                  }
                />
              </div>
            )}
          </>
        )}
      </NumberedSection>

      {/* 04 付款方式 */}
      {showCardPanel && (
        <NumberedSection
          number="04"
          data-testid="billing-payment"
          title={t('billing.paymentMethodHeading')}
          description={t('billing.paymentCardsDesc')}
        >
          {cards.length === 0 ? (
            <p className="note" data-testid="billing-cards">
              {t('billing.cardNotBound')}
            </p>
          ) : (
            <div className="card-list" data-testid="billing-cards">
              {cards.map((card) => (
                <div className="card-row" key={card.id} data-testid={`billing-card-${card.id}`}>
                  <div className="card-meta">
                    <span className="card-brand">
                      {card.cardAssociation?.toUpperCase() ?? '••••'}
                    </span>
                    <span className="card-no num">
                      {t('billing.cardLine', { last4: card.lastFourDigits ?? '????' })}
                    </span>
                    {card.expiryDate && (
                      <span className="card-exp">
                        {t('billing.cardExpiryLabel', { date: card.expiryDate })}
                      </span>
                    )}
                    {card.failedSince && <Tag>{t('billing.cardDeclined')}</Tag>}
                  </div>
                  <div className="card-acts">
                    {card.defaultCard ? (
                      <span className="card-default-tag">{t('billing.cardDefault')}</span>
                    ) : (
                      <button
                        type="button"
                        className="card-set btn btn-ghost btn-sm"
                        data-testid={`billing-card-set-default-${card.id}`}
                        onClick={() => void handleSetDefault(card.id)}
                      >
                        {t('billing.cardSetDefault')}
                      </button>
                    )}
                    <button
                      type="button"
                      className="card-remove"
                      data-testid={`billing-card-remove-${card.id}`}
                      onClick={() => void handleRemoveCard(card.id)}
                    >
                      {t('billing.cardRemove')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-[var(--gap-md)] flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              data-testid="billing-bind-card-button"
              onClick={openBindCard}
            >
              {t('billing.bindCardButton')}
            </Button>
          </div>
          {cardToast && (
            <p className="toast show" data-testid="billing-card-toast">
              {t(cardToast.key, cardToast.params)}
            </p>
          )}
        </NumberedSection>
      )}

      {/* 05 發票資訊 */}
      {showInvoicePanel && (
        <NumberedSection
          number="05"
          data-testid="billing-invoice"
          title={t('billing.invoiceHeading')}
          description={t('billing.invoiceSectionDesc')}
        >
          <InvoicePanel
            email={userEmail}
            getTokenFn={getTokenFn}
            activeContext={activeContext}
            loadFn={invoiceLoadFn}
            saveFn={invoiceSaveFn}
            reloadSignal={reloadSignal}
            embedded
          />
        </NumberedSection>
      )}

      {/* 刪除帳號 — self-serve deletion for web users (the Android app opens
       * `/account-deletion`; iOS deletes in-app). Unnumbered: the 01–05
       * sequence is what the user MANAGES, this is how they leave. */}
      <DeleteAccountSection />

      {/* ── 付款 / 新增信用卡 dialog ────────────────────────────────
       * Mounted once on first action click, then never unmounted — the
       * TapPay SDK can only `setup()` its hosted iframes once per div
       * lifetime. Closing the <dialog> hides it (display:none) while the
       * iframes stay alive in the DOM for the next open. */}
      {checkout && (
        <CheckoutDialog
          visible={modalOpen}
          onClose={() => setModalOpen(false)}
          title={checkout.title}
          subtitle={checkout.subtitle}
          dialogTestId="billing-payment-modal"
          closeTestId="billing-modal-close"
          amount={checkout.amount}
          wallet={wallet}
          coupon={checkout.coupon}
          userEmail={userEmail}
          strategy={checkout.strategy}
          note={t('billing.payNote')}
          submitLabelOverride={checkout.submitLabelOverride}
          testIdPrefix="billing"
          formTestId={checkout.formTestId}
          resetKey={checkout.resetKey}
          onSuccess={handleCheckoutSuccess}
          onCardRequired={handleCardRequired}
          redirect={redirect}
          skipScript={skipScript}
          invoiceLoadFn={invoiceLoadFn}
          invoiceSaveFn={invoiceSaveFn}
        />
      )}
    </div>
  )
}
